/**
 * Enter in a form box moves to the next box instead of submitting the form
 * (Thomas, Oct 5 2026). On a tablet the keyboard's Return/Go key looks like
 * "next", and pressing it after the first box sent a half-filled form: the
 * empty-form and missing-field errors that iPad visitors kept hitting.
 *
 * Enter still does its usual job everywhere else: a new line in the message
 * box, opening a dropdown or date button, and sending from the Send button.
 */

/** Input types where Enter means "done with this box". */
const ADVANCING_INPUT_TYPES: ReadonlySet<string> = new Set(['text', 'email', 'tel', 'number', 'search', 'url']);

/** The keyboard event fields this decision reads. */
export interface EnterKeyEvent {
  readonly key: string;
  readonly isComposing?: boolean;
  readonly shiftKey?: boolean;
  readonly ctrlKey?: boolean;
  readonly metaKey?: boolean;
  readonly altKey?: boolean;
}

/**
 * True for a bare Enter that would submit the form from a typing box. Enter
 * while an input method is composing (Japanese, Chinese) confirms the text and
 * is left alone, as is any modified Enter.
 */
export function advancesOnEnter(event: EnterKeyEvent, target: { readonly tagName: string; readonly type?: string }): boolean {
  if (event.key !== 'Enter' || event.isComposing) return false;
  if (event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return false;
  return target.tagName === 'INPUT' && ADVANCING_INPUT_TYPES.has((target.type ?? 'text').toLowerCase());
}

/** The control after `current` in tab order, or undefined when it is the last. */
export function nextInOrder<T>(controls: readonly T[], current: T): T | undefined {
  const index = controls.indexOf(current);
  return index < 0 ? undefined : controls[index + 1];
}

/** Controls Enter can move to: everything a visitor can tab to. */
export const TABBABLE_CONTROLS = 'input:not([type="hidden"]), select, textarea, button';
